import { httpBatchLink, createTRPCClient, loggerLink, TRPCClient, splitLink, httpSubscriptionLink } from '@trpc/client'
import { observable } from '@trpc/server/observable'
import { EventSourcePolyfill } from 'event-source-polyfill'

import { clientAppHeaderName } from '@campai/lib-common/entities'
import { deserialize } from '@campai/lib-common/utils/misc'
import { browserTimezone } from '@campai/lib-common/utils/date'
import type { AppRouter } from '@campai/server/routes'

import { env } from '@/env'
import { webAppState } from '@/appState'

import type { TransformerOptions } from '@trpc/client/unstable-internals'

const url = `${env.VITE_PRODUCT_ENDPOINT}/trpc`
const maxTRPCBatchURLLength = 8000

const transformer: TransformerOptions<{ transformer: true }>['transformer'] = {
    input: {
        serialize: (object) => object,
        deserialize: (object) => object,
    },
    output: {
        serialize: (object) => object,
        deserialize: (object) => {
            if (typeof object === 'string') {
                return deserialize(object)
            }
            return object
        },
    },
}

const getHeaders = async () => {
    const headers: Record<string, string> = {}

    const token = await webAppState?.auth?.getAccessToken?.()
    const clientId = webAppState?.auth?.clientId

    if (token) {
        headers['Authorization'] = `Bearer ${clientId}:${token}`
    }

    headers['Accept-Language'] = webAppState.language
    if (webAppState.clientApp) {
        headers[clientAppHeaderName] = webAppState.clientApp
    }
    headers['X-Timezone'] = browserTimezone

    return headers
}

export const client: TRPCClient<AppRouter> = createTRPCClient<AppRouter>({
    links: [
        () =>
            ({ op, next }) =>
                observable((observer) => {
                    const subscription = next(op).subscribe({
                        next(result) {
                            observer.next(result)
                            if (op.type === 'mutation' && 'data' in result.result) {
                                webAppState.onMutationSuccess?.(op.path, op.input)
                            }
                        },
                        error: (error) => observer.error(error),
                        complete: () => observer.complete(),
                    })
                    return () => subscription.unsubscribe()
                }),
        loggerLink({
            enabled: () => webAppState.isDevelopment && webAppState.debugOptions.logTRPCCalls,
        }),
        loggerLink({
            enabled: () => true,
            logger: (data) => {
                if (data.direction === 'down') {
                    const breadcrumbData: Record<string, any> = {
                        id: data.id,
                        type: data.type,
                        path: data.path,
                        input: data.input,
                        elapsedMs: data.elapsedMs,
                        method: 'POST',
                    }

                    if (!(data.result instanceof Error)) {
                        const response: any = data.result.context?.response

                        breadcrumbData.url = response?.url
                        breadcrumbData.status_code = response?.status
                        breadcrumbData.reason = response?.statusText
                    }

                    webAppState.sentry?.addBreadcrumb({
                        category: 'trpc',
                        type: 'http',
                        level: 'info',
                        data: breadcrumbData,
                        timestamp: Date.now() / 1000,
                    })
                }
            },
        }),
        splitLink({
            // uses the httpSubscriptionLink for subscriptions
            condition: (operation) => operation.type === 'subscription',
            true: httpSubscriptionLink({
                url,
                transformer,
                EventSource: EventSourcePolyfill,
                eventSourceOptions: async () => {
                    const headers = await getHeaders()
                    return { headers }
                },
            }),
            false: httpBatchLink({
                url,
                transformer,
                maxURLLength: maxTRPCBatchURLLength,
                async fetch(url, options) {
                    const response = await fetch(url, options)

                    if (response.status === 400) {
                        try {
                            // we need to clone the response to be able to read it and still return it
                            const resp = response.clone()
                            const body = await resp.json()
                            const error = JSON.parse(
                                Array.isArray(body) ? body.find((b) => b?.error)?.error : body.error
                            )
                            if (error.message === 'InvalidToken') {
                                // clear the cache and refresh the page
                                webAppState?.auth?.logout({ onlyClearSession: true })
                                window.location.reload()
                                await new Promise((resolve) => setTimeout(resolve, 5000))
                            }
                        } catch (error) {
                            // do nothing and let the trpc handle the error
                        }
                    }

                    return response
                },
                async headers() {
                    return getHeaders()
                },
            }),
        }),
    ],
})
